// Title and slug generation.

import { isHarnessId, providerIdForHarness, providerModelRegistry } from "../modelRegistry"
import { runProviderQuery } from "../nestAPIs/providerQuery"
import type { HarnessId } from "../providerEvents/harnessEventTypes"

const TITLE_QUERY_TIMEOUT_MS = 60000
const TITLE_APPEND_SYSTEM_PROMPT = "Return exactly `Title: <concise 3-5 word title>` and nothing else."

function generateRandomChars(length: number): string {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789"
    let result = ""
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
}

// Generate a random slug synchronously; no provider call needed.
export function generateSlug(): string {
    return `task-${generateRandomChars(8)}`
}

export interface GenerateTitleOptions {
    harnessId?: HarnessId | string
}

function harnessIdForTitle(harnessId: HarnessId | string | undefined): HarnessId {
    return isHarnessId(harnessId) ? harnessId : "claude-code"
}

function providerIdForTitleHarness(harnessId: HarnessId): "claude" | "codex" | "grok" | "opencode" {
    return providerIdForHarness(harnessId)
}

function lowTierTitleModel(harnessId: HarnessId): string | undefined {
    return providerModelRegistry.modelsForHarness(harnessId).find((model) => model.tier === "low")?.id
}

// Generate a title from description using Nest's ephemeral provider query path.
export async function generateTitle(description: string, abortController: AbortController, options: GenerateTitleOptions): Promise<string | null> {
    if (abortController.signal.aborted) return null

    const harnessId = harnessIdForTitle(options.harnessId)
    const prompt = `Task:\n${description}`
    const model = lowTierTitleModel(harnessId)

    try {
        const result = await runProviderQuery({
            providerId: providerIdForTitleHarness(harnessId),
            prompt,
            ...(model ? { model } : {}),
            appendSystemPrompt: TITLE_APPEND_SYSTEM_PROMPT,
            timeoutMs: TITLE_QUERY_TIMEOUT_MS,
        })
        if (abortController.signal.aborted) return null
        return cleanTitle(result.text) || null
    } catch {
        return null
    }
}

// Parse and clean a title from LLM text output.
export function cleanTitle(raw: string): string {
    const trimmed = raw.trim()
    const match = trimmed.match(/^title:\s*(.+)/im)
    if (match) {
        return match[1].trim().replace(/^["']|["']$/g, "")
    }
    return (
        trimmed
            .split("\n")
            .find((line) => line.trim())
            ?.trim()
            .replace(/^["']|["']$/g, "") ?? ""
    )
}

// Create a fallback title from description.
export function fallbackTitle(description: string): string {
    const maxLength = 50
    const cleaned = description.replace(/\s+/g, " ").trim()
    if (cleaned.length <= maxLength) {
        return cleaned
    }
    return cleaned.slice(0, maxLength).trim() + "..."
}
